/**
 * « Se connecter avec Microsoft » (Outlook, Hotmail, Live).
 *
 * Microsoft n'accepte plus de mot de passe en IMAP : il faut un jeton d'accès,
 * obtenu par OAuth 2.0 (code d'autorisation + PKCE, sans secret, comme toute
 * application qui tourne dans le navigateur). La connexion se fait dans une
 * fenêtre Microsoft ; la page public/microsoft.html y récupère le code et le
 * renvoie ici. Le jeton reste en mémoire, jamais enregistré.
 *
 * Pour activer la connexion, il faut enregistrer Scanyt chez Microsoft
 * (portail Entra, « Inscriptions d'applications ») et coller ci-dessous
 * l'« ID d'application (client) ». Voir MICROSOFT.md.
 */
export const ID_APPLICATION = '89270710-a440-4765-8e72-0c47b4a32ea1';

const AUTORITE = 'https://login.microsoftonline.com/common/oauth2/v2.0';
// Lecture de la boîte en IMAP, renouvellement du jeton, et l'adresse du compte.
const PORTEES = 'https://outlook.office.com/IMAP.AccessAsUser.All offline_access openid email profile';
const CANAL = 'scanyt-microsoft';
// Connexion par redirection de l'onglet : ce qu'il faut retrouver au retour.
const CLE_REDIRECTION = 'scanyt-microsoft-depart';
const CLE_RETOUR = 'scanyt-microsoft-retour';
const DELAI_MAX_MS = 10 * 60 * 1000;

export const microsoftConfigure = () => Boolean(ID_APPLICATION);

function base64Url(octets) {
  let binaire = '';
  for (const o of new Uint8Array(octets)) binaire += String.fromCharCode(o);
  return btoa(binaire).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function aleatoire(taille = 32) {
  return base64Url(crypto.getRandomValues(new Uint8Array(taille)));
}

function adresseRetour() {
  return new URL('/microsoft.html', location.origin).href;
}

// Annuaire des comptes Microsoft personnels (par opposition aux comptes pro).
const ANNUAIRE_PERSONNEL = '9188040d-6c67-4c5b-b112-36a304b66dad';
const DOMAINES_OUTLOOK = /@(outlook|hotmail|live|msn|windowslive|passport)\.[a-z.]+$/i;

/** Lit le jeton d'identité (sans le vérifier : il vient directement de Microsoft). */
function identiteDuJeton(idToken) {
  try {
    const charge = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const donnees = JSON.parse(decodeURIComponent(escape(atob(charge))));
    return {
      email: String(donnees.preferred_username || donnees.email || '').toLowerCase(),
      personnel: donnees.tid === ANNUAIRE_PERSONNEL
    };
  } catch {
    return { email: '', personnel: false };
  }
}

/**
 * Un compte Microsoft personnel peut être créé avec une adresse Gmail ou
 * Yahoo : il n'a alors pas de boîte Outlook, et l'IMAP est refusé.
 */
export const sansBoiteOutlook = (s) => Boolean(s && s.personnel && s.email && !DOMAINES_OUTLOOK.test(s.email));

async function demanderJeton(parametres) {
  const reponse = await fetch(`${AUTORITE}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: ID_APPLICATION, scope: PORTEES, ...parametres })
  });
  const donnees = await reponse.json().catch(() => ({}));
  if (!reponse.ok || !donnees.access_token) {
    throw new Error(donnees.error_description?.split('\r\n')[0] || 'Microsoft n’a pas délivré d’accès.');
  }
  return donnees;
}

function session(donnees, precedente = {}) {
  const identite = identiteDuJeton(donnees.id_token || '');
  return {
    email: identite.email || precedente.email || '',
    personnel: identite.email ? identite.personnel : Boolean(precedente.personnel),
    accessToken: donnees.access_token,
    refreshToken: donnees.refresh_token || precedente.refreshToken || null,
    expireLe: Date.now() + (Number(donnees.expires_in) || 3600) * 1000
  };
}

/** Attend le code renvoyé par public/microsoft.html, pour cet état précis. */
function attendreRetour(etat, fenetre) {
  return new Promise((ok, ko) => {
    const canal = new BroadcastChannel(CANAL);
    const fin = (action) => { clearTimeout(minuterie); canal.close(); action(); };
    const minuterie = setTimeout(() => fin(() => ko(new Error('La connexion Microsoft a pris trop de temps.'))), DELAI_MAX_MS);
    canal.addEventListener('message', ({ data }) => {
      if (!data || data.state !== etat) return;
      try { fenetre.close(); } catch { /* déjà fermée */ }
      if (data.error) {
        const annule = data.error === 'access_denied';
        fin(() => ko(new Error(annule ? 'Connexion annulée.' : (data.description || data.error))));
      } else {
        fin(() => ok(data.code));
      }
    });
  });
}

/**
 * Ouvre la fenêtre de connexion Microsoft. À appeler directement depuis un
 * clic : sinon le navigateur bloque la fenêtre. Renvoie
 * { email, accessToken, refreshToken, expireLe }.
 */
export async function seConnecterAvecMicrosoft({ indice } = {}) {
  if (!microsoftConfigure()) {
    throw new Error('La connexion Microsoft n’est pas encore configurée sur ce site (identifiant d’application manquant).');
  }
  // La fenêtre est ouverte tout de suite, pendant le clic ; son adresse est
  // fixée une fois le défi PKCE calculé. Si le navigateur la bloque, c'est
  // l'onglet entier qui part chez Microsoft et revient (voir reprendreRedirection).
  const fenetre = window.open('about:blank', CANAL, 'popup,width=520,height=680');

  const verificateur = aleatoire(48);
  const defi = base64Url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verificateur)));
  const etat = aleatoire(16);
  const url = new URL(`${AUTORITE}/authorize`);
  url.search = new URLSearchParams({
    client_id: ID_APPLICATION,
    response_type: 'code',
    response_mode: 'query',
    redirect_uri: adresseRetour(),
    scope: PORTEES,
    state: etat,
    code_challenge: defi,
    code_challenge_method: 'S256',
    prompt: 'select_account',
    ...(indice ? { login_hint: indice } : {})
  });
  if (!fenetre) {
    sessionStorage.setItem(CLE_REDIRECTION, JSON.stringify({ verificateur, etat }));
    location.assign(url.href);
    return new Promise(() => {}); // la page s'en va
  }
  fenetre.location.href = url.href;

  const code = await attendreRetour(etat, fenetre);
  return session(await demanderJeton({
    grant_type: 'authorization_code',
    code,
    redirect_uri: adresseRetour(),
    code_verifier: verificateur
  }));
}

/**
 * Au chargement de la page : termine une connexion faite en redirigeant
 * l'onglet (fenêtre bloquée). Renvoie la session, ou null s'il n'y en a pas.
 */
export async function reprendreRedirection() {
  let depart, retour;
  try {
    depart = JSON.parse(sessionStorage.getItem(CLE_REDIRECTION) || 'null');
    retour = JSON.parse(sessionStorage.getItem(CLE_RETOUR) || 'null');
    sessionStorage.removeItem(CLE_REDIRECTION);
    sessionStorage.removeItem(CLE_RETOUR);
  } catch { return null; }
  if (!depart || !retour || retour.state !== depart.etat) return null;
  if (retour.error) {
    throw new Error(retour.error === 'access_denied' ? 'Connexion annulée.' : (retour.description || retour.error));
  }
  return session(await demanderJeton({
    grant_type: 'authorization_code',
    code: retour.code,
    redirect_uri: adresseRetour(),
    code_verifier: depart.verificateur
  }));
}

/** Renouvelle un jeton bientôt expiré, sans rouvrir de fenêtre. */
export async function renouveler(precedente) {
  if (!precedente?.refreshToken) throw new Error('Reconnecte-toi avec Microsoft.');
  return session(await demanderJeton({
    grant_type: 'refresh_token',
    refresh_token: precedente.refreshToken
  }), precedente);
}

/** Vrai si le jeton est encore valable au moins quelques minutes. */
export const jetonValable = (s) => Boolean(s && s.accessToken && s.expireLe - Date.now() > 5 * 60 * 1000);
